import React, { useState, useMemo, useEffect } from "react";
import PrimaryModal from "../../UI/Modal/PrimaryModal";
import Loader from "../../UI/Loader/Loader";
import { toast } from "react-toastify";
import { useUserContext } from "../../../contexts/UserContext";
import { getAllAEMAdobeIO } from "../../../utils/APIs";
import { useContentfulContext } from "../../../contexts/ContentfulContext";
import AgGridWrapper from "../../UI/AgGrid";
import ViewFoldersModal from "./ViewFoldersModal";

const AEMAdobeIOModal = ({ handleClose }) => {
  const [loading, setLoading] = useState(false);
  const [aemAdobeIOData, setAEMAdobeIOData] = useState([]);
  const [selectedItem, setSelectedItem] = useState();
  const [gridApi, setGridApi] = useState(null);
  const [viewFoldersModalOpen, setViewFoldersModalOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);

  const { userData, updateDefaultAEMAdobeIO } = useUserContext();
  const { content } = useContentfulContext();

  useEffect(() => {
    getAEMAdobeIODataHandler();
  }, []);

  const getAEMAdobeIODataHandler = async () => {
    try {
      setLoading(true);
      const aemAdobe = await getAllAEMAdobeIO(
        userData.token,
        userData.selectedOrganization.org_token
      );
      const updated = aemAdobe.data.map((x) => ({
        ...x,
        default: userData.defaultAEMAdobeIO?._id == x._id ? true : false,
      }));
      setAEMAdobeIOData(updated);
      // Set the default selected item
      const defaultItem = updated.find((item) => item.default);
      if (defaultItem) setSelectedItem(defaultItem);
    } catch (error) {
      toast.error(error?.message || content?.COMMON_MESSAGE.something_wrong_msg);
    } finally {
      setLoading(false);
    }
  };

  const handleViewFolders = (integration) => {
    setSelectedIntegration(integration);
    setViewFoldersModalOpen(true);
  }

  const handleRowSelected = (event) => {
    const selectedRow = event.data;
    if (selectedItem?._id !== selectedRow._id) {
      setSelectedItem(selectedRow);
      toast.success(content.GLOBAL_SELECTOR_CONTENT.aem_adobe_io.success_msg);
      updateDefaultAEMAdobeIO(selectedRow);
    }
  };

  const CheckboxRenderer = (params) => {
    const isChecked = selectedItem?._id === params.data._id;
    return (
      <input
        type="checkbox"
        checked={isChecked}
        onChange={() => handleRowSelected(params)}
        className="cursor-pointer rounded-lg w-4 h-4"
      />
    );
  };

  const colDefs = [
    {
      headerName: "",
      field: "checkbox",
      cellRenderer: CheckboxRenderer,
      suppressMovable: true,
    },
    {
      headerName: content.GLOBAL_SELECTOR_CONTENT.aem_adobe_io.table.aem_adobe_io_name,
      field: "aem_adobe_io_name",
      cellRenderer: ({ data }) => (
        <button className="text-center block cursor-pointer text-primary">
          {data.aem_adobe_io_name}
        </button>
      ),
      filter: true,
      sortable: true,
      suppressMovable: true,
      resizable: false,
    },
    {
      headerName: content.GLOBAL_SELECTOR_CONTENT.aem_adobe_io.table.id,
      field: "id",
      filter: true,
      sortable: true,
      suppressMovable: true,
      resizable: false,
    },
    {
      headerName: content.GLOBAL_SELECTOR_CONTENT.aem_adobe_io.table.action,
      field: "actions",
      cellRenderer: ({ data }) => (
        <div className="relative inline-block text-left">
          <button
            className='text-center block cursor-pointer text-primary'
            onClick={() => handleViewFolders(data)}
          >
            {content.GLOBAL_SELECTOR_CONTENT.aem_adobe_io.table.view_cta_text}
          </button>
        </div>
      ),
      suppressMovable: true,
    }
  ];

  const onGridReady = (params) => {
    setGridApi(params.api);
  };

  const autoSizeStrategy = useMemo(() => {
    return {
      type: "fitGridWidth",
      defaultMinWidth: 40,
      columnLimits: [
        {
          colId: "aem_adobe_io_name",
          minWidth: 350,
        },
        {
          colId: "id",
          minWidth: 300,
        },
      ],
    };
  }, []);

  const onFirstDataRendered = (params) => {
    if (aemAdobeIOData.length > 0) {
      const defaultRow = aemAdobeIOData.find((row) => row.default);
      if (defaultRow) {
        setSelectedItem(defaultRow);
      }
    }
  };

  const onFilterTextBoxChanged = (event) => {
    const filterValue = event.target.value.toLowerCase();
    gridApi.setFilterModel({
      aem_adobe_io_name: { type: "contains", filter: filterValue },
    });
  };

  return (
    <PrimaryModal
      title={content.GLOBAL_SELECTOR_CONTENT.aem_adobe_io.title}
      hideModal={() => selectedItem && handleClose()}
      widthClass="max-w-5xl"
    >
      {loading && <Loader />}
      <>
      <div className="p-7 pt-1 space-y-6">
        <p>{content.GLOBAL_SELECTOR_CONTENT.aem_adobe_io.description}</p>
        <div className="flex justify-between items-center">
          <input
            type="text"
            className="block px-2.5 py-2.5 w-full text-sm text-gray-900 bg-transparent rounded-lg border border-gray-2 appearance-none focus:outline-none max-w-64"
            placeholder="Search by name"
            onChange={onFilterTextBoxChanged}
          />
        </div>
        <div
          className={"ag-theme-quartz"}
          style={{ height: "100%", width: "100%" }}
        >
          <AgGridWrapper
            tableId="global-selector-aem-adobe-io-table"
            rowData={aemAdobeIOData}
            columnDefs={colDefs}
            autoSizeStrategy={autoSizeStrategy}
            onGridReady={onGridReady}
            onFirstDataRendered={onFirstDataRendered}
            rowSelection="single"
            suppressRowClickSelection
            onRowSelected={handleRowSelected}
          />
        </div>
      </div>
        {viewFoldersModalOpen && (
          <ViewFoldersModal
            onClose={() => setViewFoldersModalOpen(false)}
            integration={selectedIntegration}
          />
        )}
      </>
    </PrimaryModal>
  );
};

export default AEMAdobeIOModal;
